import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import type { ApprovalDTO } from '@/services/request-center-api'
import { useDashboard } from '../useDashboard'
import { compareByDueThenPriority } from './PendingApprovalsWidget'

const SLA_WINDOW_MS = 24 * 60 * 60 * 1000

interface UpcomingSlaWidgetProps {
	/**
	 * Test seam — pin "now" to a deterministic clock for SLA-window
	 * boundary assertions. Defaults to `new Date()` in production.
	 */
	now?: Date
}

/**
 * "Upcoming SLA" widget — renders a focused subset of the same approvals
 * data fetched by `PendingApprovalsWidget`, filtered client-side to rows
 * whose `dueAt < now + 24h` (overdue rows included — they still need
 * triage). Issues NO additional network fetch.
 *
 * Visibility follows the same predicate as `PendingApprovalsWidget`:
 * admin OR pendingApprovals > 0 OR hadEverPendingApproval.
 *
 * Spec 4 / Group 9e — `request-center-dashboard.md`.
 */
export function UpcomingSlaWidget({ now = new Date() }: UpcomingSlaWidgetProps) {
	const { pendingApprovalsVisible, pendingApprovalsQuery } = useDashboard()

	if (!pendingApprovalsVisible) return null

	if (pendingApprovalsQuery.isError) {
		return (
			<WidgetCard title="Upcoming SLA">
				<p className="text-sm text-red-700">Couldn't load SLA data. Try refreshing the page.</p>
			</WidgetCard>
		)
	}

	if (pendingApprovalsQuery.isLoading || pendingApprovalsQuery.isPending) {
		return (
			<WidgetCard title="Upcoming SLA">
				<p className="text-sm text-gray-500">Loading…</p>
			</WidgetCard>
		)
	}

	const cutoff = now.getTime() + SLA_WINDOW_MS
	const allRows = pendingApprovalsQuery.data?.data ?? []
	const inWindow = allRows.filter((row) => isWithinSlaWindow(row, cutoff))
	const sorted = inWindow.slice().sort(compareByDueThenPriority)

	if (sorted.length === 0) {
		return (
			<WidgetCard title="Upcoming SLA">
				<p className="text-sm text-gray-500">Nothing due in the next 24h.</p>
			</WidgetCard>
		)
	}

	return (
		<WidgetCard title="Upcoming SLA">
			<ul className="divide-y divide-gray-100" aria-label="Upcoming SLA list">
				{sorted.map((row) => (
					<SlaRow key={row.id} row={row} now={now} />
				))}
			</ul>
		</WidgetCard>
	)
}

/**
 * `dueAt` is null → never in window. dueAt before cutoff (now + 24h) →
 * in window. Past dueAt → in window (overdue rows still need triage).
 */
function isWithinSlaWindow(row: ApprovalDTO, cutoff: number): boolean {
	if (!row.dueAt) return false
	const due = new Date(row.dueAt).getTime()
	return due < cutoff
}

function SlaRow({ row, now }: { row: ApprovalDTO; now: Date }) {
	const dueAt = row.dueAt ? new Date(row.dueAt) : null
	const overdue = dueAt && dueAt.getTime() < now.getTime()
	const labelClass = overdue ? 'text-red-700' : 'text-amber-700'

	return (
		<li className="px-2 py-2">
			<Link
				to={`/requests-center/approvals/${row.id}`}
				className="flex items-center justify-between gap-3 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none rounded"
			>
				<span className="font-mono text-sm font-medium text-gray-900">{row.request.requestNumber}</span>
				<span className={`text-xs ${labelClass}`}>
					{overdue ? 'Overdue' : 'Due'} {dueAt ? dueAt.toLocaleString() : ''}
				</span>
			</Link>
		</li>
	)
}

function WidgetCard({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<Card className="p-4">
			<h2 className="mb-3 text-sm font-semibold text-gray-700">{title}</h2>
			{children}
		</Card>
	)
}
